import { Link } from "react-router-dom";
import { MapPin, ChevronDown } from "lucide-react";
import { toBoroughSlug } from "@/lib/boroughUtils";
import { fetchBoroughCounts } from "@/lib/practicesService";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { groupByRegion } from "@/lib/regions";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

interface BoroughLinksProps {
  variant?: "grid" | "compact";
  limit?: number;
}

const BoroughLinks = ({ variant = "grid", limit }: BoroughLinksProps) => {
  const [showAll, setShowAll] = useState(false);
  const [openRegions, setOpenRegions] = useState<Record<string, boolean>>({});
  const { data: rows = [] } = useQuery({
    queryKey: ["borough-counts"],
    queryFn: fetchBoroughCounts,
    staleTime: 5 * 60 * 1000,
  });
  const boroughs = rows.map((b) => ({ name: b.name, slug: toBoroughSlug(b.name), count: b.count }));

  const displayed = limit && !showAll ? boroughs.slice(0, limit) : boroughs;

  if (variant === "compact") {
    return (
      <div className="space-y-1.5">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
          <MapPin className="h-3.5 w-3.5 text-primary" />
          Browse by Area
        </h3>
        <div className="flex flex-wrap gap-1.5">
          {displayed.map((b) => (
            <Link
              key={b.slug}
              to={`/area/${b.slug}`}
              className="rounded-md bg-secondary px-2 py-1 text-xs font-medium text-secondary-foreground transition-colors hover:bg-primary hover:text-primary-foreground"
            >
              {b.name}
              <span className="ml-1 opacity-60">({b.count})</span>
            </Link>
          ))}
        </div>
        {limit && !showAll && boroughs.length > limit && (
          <button
            onClick={() => setShowAll(true)}
            className="text-xs font-medium text-primary hover:underline"
          >
            Show all {boroughs.length} areas
          </button>
        )}
      </div>
    );
  }

  const regionGroups = groupByRegion(rows);
  const setRegionOpen = (name: string, open: boolean) =>
    setOpenRegions((s) => ({ ...s, [name]: open }));

  return (
    <div>
      <h2 className="text-center text-2xl font-bold text-foreground sm:text-3xl">
        Browse by Area
      </h2>
      <p className="mx-auto mt-2 max-w-xl text-center text-sm text-muted-foreground">
        Browse by region, then expand to see councils
      </p>
      <div className="mx-auto mt-8 max-w-4xl space-y-2">
        {regionGroups.map((region) => {
          const open = !!openRegions[region.name];
          const panelId = `region-panel-${toBoroughSlug(region.name)}`;
          const triggerId = `region-trigger-${toBoroughSlug(region.name)}`;
          const summary = `${region.councils.length} council${region.councils.length === 1 ? "" : "s"}, ${region.total.toLocaleString("en-GB")} practices`;
          return (
            <Collapsible
              key={region.name}
              open={open}
              onOpenChange={(o) => setRegionOpen(region.name, o)}
              className="overflow-hidden rounded-lg border border-border/60 bg-card"
            >
              <CollapsibleTrigger
                id={triggerId}
                aria-controls={panelId}
                aria-label={`${open ? "Collapse" : "Expand"} ${region.name} (${summary})`}
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <span className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary" aria-hidden="true" />
                  <span className="text-sm font-semibold text-foreground">{region.name}</span>
                  <span className="text-xs text-muted-foreground">{summary}</span>
                </span>
                <ChevronDown
                  aria-hidden="true"
                  className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                />
              </CollapsibleTrigger>
              <CollapsibleContent
                id={panelId}
                role="region"
                aria-labelledby={triggerId}
                className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
              >
                <div className="grid grid-cols-2 gap-2 border-t border-border/60 bg-background/40 p-3 sm:grid-cols-3 md:grid-cols-4">
                  {region.councils.map((c) => (
                    <Link
                      key={c.name}
                      to={`/area/${toBoroughSlug(c.name)}`}
                      aria-label={`${c.name}, ${c.count} practice${c.count === 1 ? "" : "s"}`}
                      className="group flex items-center justify-between rounded-md px-2.5 py-1.5 transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <span className="text-xs font-medium text-foreground group-hover:text-primary">
                        {c.name}
                      </span>
                      <span className="text-[11px] text-muted-foreground">{c.count}</span>
                    </Link>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </div>
    </div>
  );
};

export default BoroughLinks;
